'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useRouter, useParams } from '@nx-frontend/shared/hooks/router-proxy';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $notes } from '@nx-frontend/shared/store/notes';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $isOwner, $orgRoleState } from '@nx-frontend/shared/store/org-role';
import { $wpCanManageMembers, $wpCanManagePermissions } from '@nx-frontend/shared/store/workspace-access';
import { MenuItem, MenuItemType, PopupType, SharePopupPayload } from '@nx-frontend/shared/types';
import { NextRouterType } from '@nx-frontend/shared/utils';
import { InviteMemberButton } from '@nx-frontend/webnotes-client/shared/ui';
import { getOpenManagePopup, getOpenInvitePopup } from '@nx-frontend/widgets/popups';
import { SearchInput } from '@nx-frontend/widgets/search';
import { useOpenKitchenMenuContextFolder } from '@nx-frontend/widgets/sidebar';

export const WebnotesTopbarFolderItems: React.FC = () => {
  const [canManageMembers, wpCanManagePermissions, isOwner, currentWorkspace, notes, orgInfo, role, billingInfo] =
    useUnit([
      $wpCanManageMembers,
      $wpCanManagePermissions,
      $isOwner,
      $currentWorkspace,
      $notes,
      $orgInfo,
      $orgRoleState,
      $billingInfo,
    ]);

  const [openModal] = useUnit([openModalEvent]);

  const router = useRouter();
  const params = useParams();

  const t = useTranslations();

  const openKitchenMenuContextFolder = useOpenKitchenMenuContextFolder();

  const slug = useMemo(() => (params?.slug as string[]) || [], [params?.slug]);
  const [, , globalId] = slug || [];

  const routerType = NextRouterType.APP;
  const routerPushFn = router.push;

  const handleInviteclick = useCallback(() => {
    const openManagePopup = getOpenManagePopup(t)({ isFreePlan: !wpCanManagePermissions, isOwner, openModal });
    const currentFolder = notes.workspaces[currentWorkspace.globalId]?.entities[globalId];

    if (globalId === 'root') {
      getOpenInvitePopup(
        { orgInfo, role, billingInfo, currentWorkspace, canManageMembers: canManageMembers },
        openModal,
        { routerType, routerPushFn }
      )({
        payloadType: PopupType.INVITE,
        subjectType: 'workspace',
      });
    } else {
      openManagePopup({
        payloadType: PopupType.MANAGE,
        subjectType: 'folder',
        globalId: currentFolder.globalId,
        parentId: currentFolder.parentId,
        title: currentFolder.title,
        isDefaultOrInside: false,
      });
    }
  }, [
    billingInfo,
    canManageMembers,
    currentWorkspace,
    globalId,
    isOwner,
    notes.workspaces,
    openModal,
    orgInfo,
    role,
    routerPushFn,
    routerType,
    wpCanManagePermissions,
    t,
  ]);

  const handleShareClick = useCallback(() => {
    const sharePopupPayload: SharePopupPayload = {
      payloadType: PopupType.SHARE,
      globalId,
    };

    openModal({
      content: PopupType.SHARE,
      showCloseButton: false,
      payload: sharePopupPayload,
      usePadding: false,
    });
  }, [globalId, openModal]);

  const folderMenuItem: MenuItem | undefined = useMemo(() => {
    const folder = notes.workspaces[currentWorkspace.globalId]?.entities[globalId];
    if (folder) {
      return {
        id: `${MenuItemType.NOTES_FOLDER}#${folder.globalId}`,
        type: MenuItemType.NOTES_FOLDER,
        globalId: folder.globalId,
        workspaceId: folder.workspaceId,
        name: folder.title,
        icon: folder.emoji || 'folder',
        userId: folder.userId,
        updatedAt: folder.updatedAt,
        createdAt: folder.createdAt,
        parentId: `${MenuItemType.NOTES_FOLDER}#${folder.parentId}`,
        index: 0,
        visible: true,
        url: '',
        slug: '',
        openInNewTab: false,
        hasChildren: true,
        shared: folder.shared,
        targetId: '',
      };
    }
  }, [notes, currentWorkspace.globalId, globalId]);

  const handleMoreButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!folderMenuItem) return;
      openKitchenMenuContextFolder(folderMenuItem, e, true);
    },
    [folderMenuItem, openKitchenMenuContextFolder]
  );

  return (
    <div className="flex items-center gap-4">
      <div className="min-w-[162px]">
        <SearchInput placeholder={t('webnotes.webnotes-search_pages')} />
      </div>
      <div className="flex items-center gap-2">
        {canManageMembers && (
          <InviteMemberButton
            onClick={handleInviteclick}
            buttonType={globalId === 'root' ? 'folders_root' : 'folders'}
            orgRolePrivileges={role.privileges}
          />
        )}
        {globalId !== 'root' && (
          <>
            <Button
              size={ButtonSize.XL2}
              type={ButtonType.STROKED}
              data-cy="folders-share-button"
              onClick={handleShareClick}
              isIcon={true}
            >
              <Icon icon={IconsMap.ARROW_REPLY_1} className="text-cyan-900 w-[16px] h-[16px]" />
            </Button>
            <button
              data-cy="folders-context-menu-button"
              onClick={handleMoreButtonClick}
              className="flex w-[30px] h-[30px] justify-center items-center rounded hover:bg-zinc-50"
            >
              <Icon icon={IconsMap.MORE_HORIZONTAL} className="text-cyan-900 w-[16px] h-[16px]" />
            </button>
          </>
        )}
      </div>
    </div>
  );
};
